import { createPortal } from "react-dom";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
// Redux
import { useSelector } from "react-redux";
// Components
import { LuSearch, LuGrid2X2, LuImage, LuStar } from "react-icons/lu";
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function.js";
import { CartActions } from "../../components/cartActions";
import "./style.css";
import { ProductDetails } from "./product/productDetails";
import { getProduct } from "../../api/commander";
import { productPriceWithTax } from "../../utils/pricing";

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const FloatModal = ({ content }) => {
  const overlay = <div className="shadowBackground productDetails-modal-overlay">{content}</div>;
  const workspace = document.querySelector(".tpvWorkspace");
  return workspace ? createPortal(overlay, workspace) : overlay;
};

const ProductItem = ({ data, productQuantities, favorites, toggleFavorite, detailPending, handleItem, canAddProducts }) => {
    const quantity = productQuantities.get(String(data.productId)) || 0;
    const previousQuantity = useRef(quantity);
    const [pulse, setPulse] = useState(0);
    useEffect(() => {
      if (quantity > previousQuantity.current) setPulse(value => value + 1);
      previousQuantity.current = quantity;
    }, [quantity]);
    const image = data?.images?.[0];
    const price = productPriceWithTax(data);
    const quantityInCart = productQuantities.get(String(data.productId)) || 0;
    return (
      <article className={`productItem${quantityInCart > 0 ? " productItem--in-cart" : ""}${pulse ? " productItem--added" : ""}`}>
        {pulse > 0 && <span key={pulse} className="tpvAddedFeedback" aria-hidden="true" onAnimationEnd={() => setPulse(0)}>✓</span>}
        {quantityInCart > 0 && (
          <span className="productItemQuantity" aria-label={`${quantityInCart} en el pedido`}>
            {quantityInCart}
          </span>
        )}
        <button type="button" className={`tpvFavoriteToggle ${favorites.includes(data.productId)?'is-favorite':''}`} aria-label={`Favorito: ${data.shortName||data.name}`} aria-pressed={favorites.includes(data.productId)} onClick={()=>toggleFavorite(data.productId)}><LuStar aria-hidden="true"/></button>
        {(data.hasOptions||data.optionsGroups?.length||data.variants?.length||data.itemGroups?.length)?<span className="tpvProductOptionBadge">OPCIONES</span>:null}
        <CartActions data={data} renderAdd={(add) => <button
          type="button"
          className="tpvProductOpen"
          aria-label={`Añadir ${data.shortName || data.name}`}
          disabled={detailPending || !canAddProducts}
          onClick={() => handleItem(data, add)}
        >
          <span className="productItemImg">
            {image ? <img src={image} alt="" loading="lazy" /> : <LuImage aria-hidden="true" />}
          </span>
          <span className="productItemName">{data.shortName || data.name}</span>
          <span className="tpvProductPrice">
            {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(price)}
          </span>
        </button>} />
      </article>
    );
  };

export const Catalog = () => {
  const station = useSelector(state=>state.aplicationConfig.corporationData?.pointConfig?.PointOfSaleConfigurationId||'default');
  const favoriteKey=`delibran.tpv.favorites.${station}`;
  const [favorites,setFavorites]=useState([]);
  useEffect(()=>{try{const stored=JSON.parse(localStorage.getItem(favoriteKey)||'[]');setFavorites(Array.isArray(stored)?stored:[]);}catch{setFavorites([]);}},[favoriteKey]);
  const toggleFavorite=id=>{setFavorites(previous=>{const next=previous.includes(id)?previous.filter(value=>value!==id):[...previous,id];try{localStorage.setItem(favoriteKey,JSON.stringify(next));}catch{ /* Favorites remain available for this session if storage is blocked. */ }return next;});};
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [categoryName, setCategoryName] = useState("Todos los productos");
  const detailInFlight = useRef(false);
  const [detailPending, setDetailPending] = useState(false);
  const [detailError, setDetailError] = useState("");

  const allProducts = useSelector((state) => state.aplicationConfig.corporationData.products);
  const categories = useSelector((state) => state.aplicationConfig.corporationData.categories);
  const cart = useSelector((state) => state.aplicationConfig.userConfig.cart);
  const order = useSelector((state) => state.aplicationConfig.userConfig.order);
  const client = useSelector((state) => state.aplicationConfig.userConfig.client);
  const selectedShippingMethodId = useSelector((state) => state.aplicationConfig.userConfig.selectedShippingMethodId);
  const shippingMethods = useSelector((state) => state.aplicationConfig.corporationData.pointMethods.shipping);

  const [products, setProducts] = useState([]);
  const [productDetailsModal, setProductDetailsModal] = useState(null);

  const safeCategories = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);
  const selectedShippingMethod = (Array.isArray(shippingMethods) ? shippingMethods : []).find(
    (method) => String(method?.shippingMethodId) === String(selectedShippingMethodId),
  );
  const isDefaultClient =
    String(client?.fullname || "")
      .trim()
      .toLowerCase() === "cliente contado";
  const deliveryClientReady =
    !selectedShippingMethod?.homeDeliveryIsActive || (Boolean(client?.customerId) && !isDefaultClient);
  const canAddProducts =
    (Boolean(client?.customerId) || Boolean(order?.orderId)) && deliveryClientReady && (!order?.orderId || (order?.status === "pending" && order?.paymentStatus !== "paid" && (order.products || []).every(line => line.status === "pending")));

  // Cantidad total por productId. Si hay un pedido pendiente activo, el contador
  // se calcula con sus productos; en caso contrario se mantiene la lógica del carrito.
  // Se ignoran variantes, complementos y notas para mostrar el total real del producto.
  const productQuantities = useMemo(() => {
    const quantities = new Map();
    const isOrder = Boolean(order?.orderId);
    const sourceItems = isOrder
      ? Array.isArray(order?.products)
        ? order.products
        : []
      : Array.isArray(cart?.ShoppingCartProducts)
        ? cart.ShoppingCartProducts
        : [];

    sourceItems.forEach((item) => {
      const productId = item?.productId ?? item?.Product?.productId ?? item?.Product?.id ?? item?.id;
      if (productId == null) return;

      const key = String(productId);
      const quantity = Math.max(0, toNumber(item?.quantity));
      quantities.set(key, (quantities.get(key) || 0) + quantity);
    });

    return quantities;
  }, [order?.orderId, order?.products, cart?.ShoppingCartProducts]);

  const handleCategory = useCallback(
    (category) => {
      const list = category?.isAll
        ? Array.isArray(allProducts)
          ? allProducts
          : []
        : Array.isArray(category?.Products)
          ? category.Products
          : [];
      const mutableArray = list.map((p) => ({ ...p }));
      const withIds = addIdKeyIfMissing(mutableArray);
      setProducts(withIds);
      setActiveCategory(category?.categoryId || category?.id || category?.name);
      setCategoryName(category?.name || "Todos los productos");
    },
    [allProducts],
  );

  const CategoryItem = ({ data }) => (
    <button
      type="button"
      className={`categoryItem ${activeCategory === (data.categoryId || data.id || data.name) ? "is-active" : ""}`}
      aria-pressed={activeCategory === (data.categoryId || data.id || data.name)}
      onClick={() => handleCategory(data)}
    >
      {data.isAll && <LuGrid2X2 aria-hidden="true" />}
      <span>{data.name}</span>
    </button>
  );

  const handleItem = async (data, add) => {
    if (!canAddProducts) return;
    if (detailInFlight.current) return;
    detailInFlight.current = true;
    setDetailPending(true);
    setDetailError("");
    try {
      const response = await getProduct(data.productId);
      if (response?.error !== false || String(response.data?.productId) !== String(data.productId)) throw new Error("unavailable");
      const product = response.data;
      const configurable = Boolean(product.hasOptions || product.optionsGroups?.length || product.variants?.length || product.itemGroups?.some(group => group.isActive !== false && group.items?.length));
      if (configurable) setProductDetailsModal(product);
      else add();
    } catch {
      setDetailError("No se pudo abrir el producto. Vuelve a intentarlo.");
    } finally {
      detailInFlight.current = false;
      setDetailPending(false);
    }
  };
  const visibleProducts = products.filter(
    (product) =>
      product.status === "PUBLISHED" && (activeCategory!=="favorites"||favorites.includes(product.productId)) &&
      `${product.shortName || ""} ${product.name || ""} ${product.reference || ""}`
        .toLocaleLowerCase("es")
        .includes(search.toLocaleLowerCase("es")),
  );


  // Sync productos desde Redux (evita que se quede con el valor inicial)
  useEffect(() => {
    const base = Array.isArray(allProducts) ? allProducts : [];
    const withIds = addIdKeyIfMissing(base);
    setProducts(withIds);
  }, [allProducts]);

  return (
    <>
      <div className="categories">
        <div className="categories-body-content">
          <button type="button" className={`categoryItem ${activeCategory==='favorites'?'is-active':''}`} onClick={()=>handleCategory({id:'favorites',name:'Favoritos',isAll:true})}><LuStar aria-hidden="true"/> Favoritos</button>
          <CategoryItem data={{ id: "all", name: "Todos", isAll: true }} />
          {safeCategories
            .filter((f) => f?.isActive === true)
            .map((item) => (
              <CategoryItem key={item.id || item._id || item.name} data={item} />
            ))}
        </div>
      </div>

      <div className="products">
        <div className="tpvCatalogToolbar">
          <label className="tpvSearch">
            <LuSearch aria-hidden="true" />
            <input
              type="search"
              aria-label="Buscar producto"
              placeholder="Buscar producto…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="tpvCatalogHeading">
            <h1>{categoryName}</h1>
            <span>{visibleProducts.length} productos</span>
          </div>
          {detailPending && <p role="status">Abriendo producto…</p>}
          {detailError && <p role="alert">{detailError}</p>}
        </div>
        <div className="products-body-content">
          {visibleProducts.map((item) => (
            <ProductItem key={item.productId || item.id || item._id || item.name} data={item} productQuantities={productQuantities} favorites={favorites} toggleFavorite={toggleFavorite} detailPending={detailPending} handleItem={handleItem} canAddProducts={canAddProducts} />
          ))}
          {!visibleProducts.length && (
            <p className="tpvNoResults">{activeCategory==='favorites'?'Marca la estrella de un producto para tenerlo a mano en este puesto.':'No hay productos que coincidan con esta selección.'}</p>
          )}
        </div>
      </div>

      {productDetailsModal !== null && (
        <FloatModal
          content={
            <ProductDetails productDetailsModal={productDetailsModal} setProductDetailsModal={setProductDetailsModal} />
          }
        />
      )}
    </>
  );
};
